"use client";

import { Suspense, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";

const PIXEL_ID = "1886948638620648";

type FbqWithInternal = typeof window.fbq & {
  callMethod?: (...args: unknown[]) => void;
  __leadPatched?: boolean;
};

function MetaPixelTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    import("react-facebook-pixel")
      .then((ReactPixel) => {
        ReactPixel.default.init(PIXEL_ID, undefined, {
          autoConfig: false,
          debug: false,
        });

        // Facebook's pixel plugin fires Lead via fbq.callMethod internally.
        // Polling misses it because fbevents.js sets callMethod and fires Lead
        // within the same tick. Use Object.defineProperty to intercept the
        // moment callMethod is assigned — zero polling delay.
        const fbq = window.fbq as FbqWithInternal | undefined;
        if (fbq && !fbq.__leadPatched) {
          let _callMethod: FbqWithInternal["callMethod"];
          Object.defineProperty(fbq, "callMethod", {
            configurable: true,
            get() {
              return _callMethod;
            },
            set(fn: FbqWithInternal["callMethod"]) {
              _callMethod = fn
                ? function (...args: unknown[]) {
                    if (args[0] === "track" && args[1] === "Lead") return;
                    return fn.apply(fbq, args);
                  }
                : fn;
              (window as unknown as Record<string, unknown>).__fbqTrackLead = () => fn?.call(fbq, "track", "Lead");
            },
          });
          fbq.__leadPatched = true;
        }

        ReactPixel.default.pageView();
      })
      .catch((error) => {
        console.warn("[MetaPixel] Initialization failed:", error);
      });
  }, []);

  useEffect(() => {
    import("react-facebook-pixel")
      .then((ReactPixel) => ReactPixel.default.pageView())
      .catch((error) => {
        console.warn("[MetaPixel] PageView tracking failed:", error);
      });
  }, [pathname, searchParams]);

  return null;
}

export default function MetaPixelInit() {
  return (
    <Suspense fallback={null}>
      <MetaPixelTracker />
    </Suspense>
  );
}
